/**
 * NavigationBar.tsx - Universal Navigation Bar Component
 * 
 * STANDARD COMPONENT - Should be included in every OpCentral app
 * 
 * Features:
 * - Back/Forward buttons
 * - Breadcrumbs
 * - Navigation history dropdown
 * - Keyboard shortcuts (Alt+Left/Right)
 */

import React, { useEffect } from 'react';
import { useNavigatr } from '../useNavigatr';
import './NavigationBar.css';

export interface NavigationBarProps {
  /** App name for Navigatr instance */
  appName: string;
  
  /** Show back/forward buttons */
  showBackForward?: boolean;
  
  /** Show breadcrumbs */
  showBreadcrumbs?: boolean;
  
  /** Show history dropdown */
  showHistory?: boolean;
  
  /** Custom class name */
  className?: string;
  
  /** Callback when navigation occurs */
  onNavigate?: (route: string) => void;
  
  /** Custom breadcrumb labels */
  breadcrumbLabels?: Record<string, string>;
}

export const NavigationBar: React.FC<NavigationBarProps> = ({
  appName,
  showBackForward = true,
  showBreadcrumbs = true,
  showHistory = false,
  className = '',
  onNavigate,
  breadcrumbLabels = {},
}) => {
  const { navigatr } = useNavigatr(appName) as any;
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [canGoBack, setCanGoBack] = React.useState(false);
  const [canGoForward, setCanGoForward] = React.useState(false);
  const [breadcrumbs, setBreadcrumbs] = React.useState<any[]>([]);
  const [history, setHistory] = React.useState<any[]>([]);

  // Update state when navigation changes
  useEffect(() => {
    if (!navigatr) return;
    
    const updateState = () => {
      setCanGoBack(navigatr.canGoBack());
      setCanGoForward(navigatr.canGoForward());
      setBreadcrumbs(navigatr.getBreadcrumbs());
      setHistory(navigatr.getNavigationHistory());
    };

    updateState();

    // Poll for changes (could be improved with events)
    const interval = setInterval(updateState, 100);
    return () => clearInterval(interval);
  }, [navigatr]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && e.key === 'ArrowLeft') {
        e.preventDefault();
        handleBack();
      } else if (e.altKey && e.key === 'ArrowRight') {
        e.preventDefault();
        handleForward();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleBack = () => {
    if (!navigatr) return;
    const result = navigatr.back();
    if (result && onNavigate) {
      onNavigate(result.route || result.type);
    }
  };

  const handleForward = () => {
    if (!navigatr) return;
    const result = navigatr.forward();
    if (result && onNavigate) {
      onNavigate(result.route || result.type);
    }
  };

  const handleBreadcrumbClick = (route: string) => {
    if (onNavigate) {
      onNavigate(route);
    }
  };

  const handleHistoryClick = (index: number) => {
    if (!navigatr) return;
    navigatr.goToIndex(index);
    setHistoryOpen(false);
    const item = history[index];
    if (item && onNavigate) {
      onNavigate(item.route || item.type);
    }
  };

  return (
    <div className={`navigation-bar ${className}`}>
      {showBackForward && (
        <div className="navigation-controls">
          <button
            className="nav-button nav-back"
            onClick={handleBack}
            disabled={!canGoBack}
            title="Go back (Alt+Left)"
            aria-label="Go back"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M10 2L4 8l6 6V2z" />
            </svg>
          </button>
          
          <button
            className="nav-button nav-forward"
            onClick={handleForward}
            disabled={!canGoForward}
            title="Go forward (Alt+Right)"
            aria-label="Go forward"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M6 2l6 6-6 6V2z" />
            </svg>
          </button>

          {showHistory && history.length > 0 && (
            <div className="nav-history-dropdown">
              <button
                className="nav-button nav-history"
                onClick={() => setHistoryOpen(!historyOpen)}
                title="Navigation history"
                aria-label="Show navigation history"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M2 4h12M2 8h12M2 12h12" />
                </svg>
              </button>
              
              {historyOpen && (
                <div className="nav-history-menu">
                  {history.map((item, index) => (
                    <button
                      key={index}
                      className={`nav-history-item ${index === navigatr.currentIndex ? 'active' : ''}`}
                      onClick={() => handleHistoryClick(index)}
                    >
                      {item.label || item.route || item.type}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showBreadcrumbs && breadcrumbs.length > 0 && (
        <div className="navigation-breadcrumbs">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span className="breadcrumb-separator">/</span>}
              <button
                className={`breadcrumb-item ${index === breadcrumbs.length - 1 ? 'active' : ''}`}
                onClick={() => handleBreadcrumbClick(crumb.route)}
                disabled={index === breadcrumbs.length - 1}
              >
                {breadcrumbLabels[crumb.label] || crumb.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};
